// The WGSL twin of `../heatmap.ts`.
//
// It is the same picture from the same figures: read the cell, bucket the
// intensity, paint it. A reader holds the two files side by side, because the
// twin keeps the names, the order and the arithmetic of the GLSL.
//
// Two rules of the port show up here:
//
//  * the plane tap is `textureLoad`, which needs no sampler and answers the
//    texel, and the `inGrid` guard stays an `if` because `select` evaluates
//    BOTH arms;
//  * the ring walk's `(i == 0) ? c0 : neighbor(c0, i - 1)` is an `if` for the
//    same reason. `select` would evaluate `neighbor(c0, -1)` as well, and the
//    unrolled loop then indexes the direction table at -1, which no compiler
//    accepts.
//
// A twin declares NO uniform of its own. The whole block is generated from
// the spec list in front of the body, so every read is `U.uName`.

import { HEAT_ALL_UNIFORMS } from '../heatmap';
import { PRELUDE_BODY_WGSL } from './prelude';
import { wgslModule } from './module';

/**
 * The heatmap's own body and its fragment entry.
 *
 * It is the twin of the `BODY` half of `../heatmap.ts` alone: the header and
 * the shared prelude are generated and concatenated by `heatProgramWgsl`
 * below, the way the GLSL half concatenates `PRELUDE`.
 */
const HEAT_FRAG_WGSL = `
fn intensityAt(c: vec2i) -> f32 {
  if (!inGrid(c)) { return 0.0; }
  return textureLoad(uHeat, c, 0).r * 255.0;
}

@fragment fn fs(v: VsOut) -> @location(0) vec4f {
  let p = v.vWorld;
  let c0 = cellAt(p);
  // The tint covers water and only water (R103): rock is never owned,
  // fought over or worked, so a ridge cell is dropped rather than drawn.
  if (!inGrid(c0) || kindOf(c0) == 2) { return vec4f(0.0); }

  var v0 = 0.0;
  if (U.uHeatSmooth == 1) {
    // The early-out (R3082, R493): read the ring first. When the cell and
    // its six neighbours all hold intensity 0, the weighted sum is 0, so the
    // pixel draws nothing, and the kernel taps are skipped. Only the
    // intensity reads stay; the result does not change by one bit.
    var cs: array<vec2i, 7>;
    var hs: array<f32, 7>;
    var hmax = 0.0;
    for (var i: i32 = 0; i < 7; i++) {
      // The GLSL takes the centre cell with a ternary. WGSL evaluates both
      // arms of a select, and the dead arm would ask for direction -1.
      var c = c0;
      if (i > 0) { c = neighbor(c0, i - 1); }
      cs[i] = c;
      hs[i] = intensityAt(c);
      hmax = max(hmax, hs[i]);
    }
    if (hmax == 0.0) { return vec4f(0.0); }

    let invR2 = 1.0 / (U.uHeatRadius * U.uHeatRadius);
    var num = 0.0;
    var den = 0.0;
    for (var i: i32 = 0; i < 7; i++) {
      if (kindOf(cs[i]) == 2) { continue; }
      var k = 0.0;
      var dk = vec2f(0.0);
      kern(p, cellCenter(cs[i]), invR2, &k, &dk);
      num += k * hs[i];
      den += k;
    }
    if (den > 1e-6) { v0 = num / den; }
  } else {
    v0 = intensityAt(c0);
  }

  // The server saying "nothing here" is intensity 0, and it draws nothing.
  if (v0 < 1.0) { return vec4f(0.0); }

  let B = f32(U.uHeatBuckets);
  let b = min(floor((v0 - 1.0) * B / 255.0), B - 1.0);
  var t = 1.0;
  if (U.uHeatBuckets > 1) { t = b / (B - 1.0); }

  var tint = U.uHeatColor;
  if (U.uHeatHasDark == 1) {
    tint = mix(U.uHeatDark, U.uHeatColor, pow(t, U.uHeatDarkEase));
  }
  let a = U.uHeatAlphaLo + (U.uHeatAlphaHi - U.uHeatAlphaLo) * t;
  return vec4f(tint * a, a);
}
`;

/**
 * The whole heatmap module: the header, the shared prelude and the body.
 *
 * It carries NO vertex entry, and it declares the pass's own block: it is
 * the module the parity suite holds the twin against. The engine builds its
 * module through `worldProgram` instead, from the whole block with the
 * driver's three uniforms first, because the shared vertex twin reads them.
 */
export function heatProgramWgsl(): string {
  return wgslModule({ uniforms: HEAT_ALL_UNIFORMS, body: heatBodyWgsl() });
}

/**
 * The heatmap's BODY: the shared prelude body and the fragment entry.
 *
 * `../heatmap.ts` `heatProgram` hands it to `worldProgram`, which puts the
 * generated header and the shared world vertex entry in front of it. That is
 * the one assembly rule of the engine (`./module.ts`).
 */
export function heatBodyWgsl(): string {
  return PRELUDE_BODY_WGSL + HEAT_FRAG_WGSL;
}
